import {
  EMAIL_TEMPLATE_CONTENT,
  EMAIL_TEMPLATES,
  REFERRAL_SUBJECT_LINES,
  TEXT_MESSAGE_TEMPLATES,
  WEB_SURVEYS_BY_EMAIL_TEXT_CONTENT,
  WEB_SURVEY_BY_EMAIL_TEMPLATE,
  EMAIL_TEXT_NAME_MATCH,
  REFERRAL_SURVEYS_TO_EMAIL,
  retiredEmails,
  CHC_ACT_REFERRAL_PARTNER_INTAKE_EMAIL_SUBJECTS,
  getReferralPartnerSummaryTemplate,
  getReferralPartnerTextTemplate,
  getStateSummaryTemplate,
  getStateTextTemplate,
  TEXT_MESSAGE_TEMPLATES_PINPOINT,
} from 'fhir-sdoh/common/constants';
import { mapKey } from 'utils';
/**
 * The flattened patient fields this resolver reads.
 *
 * `state` is `unknown[]`, matching `FlatPatient.state` — the raw
 * `fhirpath.evaluate` collection, so array-wrapped with no known element type.
 *
 * `referralPartner` stays optional here though `FlatPatient` declares it
 * required: this resolver only reads it, and an optional parameter accepts the
 * required field.
 */
interface CommunicationPatient {
  state?: unknown[];
  referralPartner?: string;
}

export const EMAIL_TEXT_SUMMARY = (
  commType: string,
  commSubject: string | undefined,
  org = 'GENERIC',
  source: string | undefined = undefined,
  patient: CommunicationPatient | undefined
) => {
  /*
   * Both are assigned in every reachable branch before use, but not on all paths
   * the compiler can see, so they stay definitely-unassigned rather than being
   * pre-seeded with a placeholder that would change what the caller receives
   * when no branch matches.
   */
  let emailOrTextTemplate: string | undefined;
  let emailTemplateId: string | undefined;
  //extract email subject
  const separatorIndex: number | undefined = commSubject?.indexOf(' | ');
  /*
   * `unknown`, because `FlatPatient.state` is the raw `fhirpath.evaluate`
   * collection and nothing narrows its elements. The `getState*Template`
   * helpers declare a `string` parameter, so each call asserts: they key a
   * lookup table with this value, and a non-string misses and yields the
   * default template. Narrowing here would change which template a malformed
   * state produces.
   */
  const patientState = patient?.state?.[0];
  const referralPartner = patient?.referralPartner;
  if (commSubject) {
    commSubject = (separatorIndex !== -1 ? commSubject.substring(0, separatorIndex) : commSubject).trim();
    if (commType === 'email' || commType === 'email-survey') {
      if (source === 'web based survey') {
        emailOrTextTemplate = WEB_SURVEY_BY_EMAIL_TEMPLATE;
      } else if (commSubject in EMAIL_TEMPLATES) {
        emailTemplateId = EMAIL_TEMPLATES[commSubject] as string;
        //if email template id is separated by comma, resolve by referral partner or patient state
        if (emailTemplateId?.includes(',')) {
          emailOrTextTemplate = (CHC_ACT_REFERRAL_PARTNER_INTAKE_EMAIL_SUBJECTS.has(commSubject)
            ? getReferralPartnerSummaryTemplate(referralPartner, emailTemplateId)
            : getStateSummaryTemplate(patientState as string, emailTemplateId)) as string;
        } else {
          emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT[emailTemplateId] as string;
        }
      } else if (commSubject === 'Nutrition Counseling') {
        emailTemplateId = (EMAIL_TEMPLATES['CALENDLY'] as Record<string, string>)[org];
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT[mapKey(emailTemplateId)] as string;
      } else if (commSubject in REFERRAL_SURVEYS_TO_EMAIL) {
        if (commSubject === 'pre-consult-nutrition-MA') {
          const updatedEmailTitle = REFERRAL_SURVEYS_TO_EMAIL[commSubject];
          emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT[updatedEmailTitle] as string;
        } else {
          const updatedEmailTitle = REFERRAL_SURVEYS_TO_EMAIL[commSubject] as string;
          emailTemplateId = EMAIL_TEMPLATES[updatedEmailTitle] as string;
          emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT[emailTemplateId] as string;
        }
      } else if (commSubject in EMAIL_TEXT_NAME_MATCH) {
        const updatedEmailTitle = EMAIL_TEXT_NAME_MATCH[commSubject] as string;
        emailTemplateId = EMAIL_TEMPLATES[updatedEmailTitle] as string;
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT[emailTemplateId] as string;
      } else if (commSubject in REFERRAL_SUBJECT_LINES) {
        emailOrTextTemplate = REFERRAL_SUBJECT_LINES[commSubject];
      } else if (commSubject in WEB_SURVEYS_BY_EMAIL_TEXT_CONTENT) {
        emailOrTextTemplate = WEB_SURVEY_BY_EMAIL_TEMPLATE;
      } else if (commSubject in retiredEmails) {
        emailTemplateId = retiredEmails[commSubject];
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT[mapKey(emailTemplateId)] as string;
      } else if (commSubject.startsWith('Food Ratings')) {
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT['d-a41332609a244ef8b872aea76094346d'];
      } else if (commSubject === 'SunBasket Order Shipped') {
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT['d-c567e24ed7dd4af1bef39225687803b6'];
      } else if (commSubject === 'SunBasket Order Placed') {
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT['d-08f93fa2238443948636d8337e443988'];
      } else if (commSubject === 'Performance Kitchen Order Shipped') {
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT['d-0ce8a51ad2cb47c58c0bec3af8498df7'];
      }
      //Your NRx ${orderUpdateEvent["foodTypeName"]} Order Shipped! email
      // FOOD_ORDER_SHIPPED (with tracking URL and/or tracking code). No-tracking uses the same subject.
      else if (commSubject.startsWith('Your NRx') && commSubject.endsWith('Order Shipped!')) {
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT['d-babae7904bc040fca3f938dddc0c33da'];
      } else if (commSubject.startsWith('Estimated Delivery Date')) {
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT['d-6d6f8399a3044a5298230cddcc1ca46f'];
      }
      //for old email title such as: ["Auto BeWell Kickstart Repeat Intake Survey", "Auto BeWell Monthly Groceries Repeat Intake Survey", "BeWell Kickstart Intake Survey", "BeWell Kickstart Pantry Repeat Intake Survey", "BeWell Kickstart Repeat Intake Survey", "BeWell Monthly #1 Repeat Intake Survey", "BeWell Monthly #2 Intake Survey", "BeWell Monthly #2 Repeat Intake Survey","BeWell Monthly Groceries Intake Survey", "BeWell Monthly Groceries Repeat Intake Survey", "BeWell Nutrition Consult Intake Survey","BeWell Nutrition Consult Repeat Intake Survey"]
      else if (commSubject.includes('Bewell') && commSubject.includes('Intake Survey')) {
        emailTemplateId = EMAIL_TEMPLATES['d-262400ae5d9f433fa7a99cc9031138e1'] as string;
        emailOrTextTemplate = EMAIL_TEMPLATE_CONTENT[mapKey(emailTemplateId)] as string;
      } else {
        emailOrTextTemplate = 'This email temmplate is not available';
      }
    } else if (commType === 'sms' || commType === 'sms-survey') {
      if (source === 'web based survey') {
        // `[commSubject]` wraps the subject in an array before concatenating; it works
        // only because a one-element array stringifies to its element. Kept verbatim —
        // unwrapping it is a behaviour change, however inert it looks.
        emailOrTextTemplate =
          'DO NOT REPLY. Please answer your ' + ([commSubject] as unknown as string) + ' survey. <Survey Link>';
      } else if (commSubject in REFERRAL_SUBJECT_LINES) {
        emailOrTextTemplate = REFERRAL_SUBJECT_LINES[commSubject];
      } else if (commSubject in TEXT_MESSAGE_TEMPLATES) {
        if (CHC_ACT_REFERRAL_PARTNER_INTAKE_EMAIL_SUBJECTS.has(commSubject)) {
          emailOrTextTemplate = getReferralPartnerTextTemplate(referralPartner, commSubject);
        } else if (TEXT_MESSAGE_TEMPLATES_PINPOINT?.[commSubject]?.includes(',')) {
          //if text template id is separated by comma, we need to get correct template according to patient state
          emailOrTextTemplate = getStateTextTemplate(patientState as string, commSubject);
        } else {
          emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES[commSubject] as string;
        }
      } else if (commSubject in WEB_SURVEYS_BY_EMAIL_TEXT_CONTENT) {
        emailOrTextTemplate = WEB_SURVEYS_BY_EMAIL_TEXT_CONTENT[commSubject];
      } else if (commSubject.startsWith('Food Ratings')) {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['Food Ratings'];
      } else if (commSubject.startsWith('Food Order #')) {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['Food Ordered'];
      } else if (commSubject.startsWith('Your NRx') && commSubject.endsWith('Order Shipped!')) {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['Your NRx Order Shipped!'];
      }
      //for old email title such as: ["Auto BeWell Kickstart Repeat Intake Survey", "Auto BeWell Monthly Groceries Repeat Intake Survey", "BeWell Kickstart Intake Survey", "BeWell Kickstart Pantry Repeat Intake Survey", "BeWell Kickstart Repeat Intake Survey", "BeWell Monthly #1 Repeat Intake Survey", "BeWell Monthly #2 Intake Survey", "BeWell Monthly #2 Repeat Intake Survey","BeWell Monthly Groceries Intake Survey", "BeWell Monthly Groceries Repeat Intake Survey", "BeWell Nutrition Consult Intake Survey","BeWell Nutrition Consult Repeat Intake Survey"]
      else if (commSubject.includes('Bewell') && commSubject.includes('Intake Survey')) {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['Bewell Intake Survey'];
      } else if (commSubject.startsWith('Hello! We have received your NRx BeWell order.')) {
        const index = commSubject.indexOf(':');
        const orderTyoe = commSubject.slice(index + 2);
        emailOrTextTemplate = `${TEXT_MESSAGE_TEMPLATES['Hello! We have received your NRx BeWell order.']} ${orderTyoe}`;
      } else if (
        commSubject.startsWith(
          'Hello! Your NRx order is confirmed and you will be receiving your BeWell Monthly #2 meals soon'
        )
      ) {
        emailOrTextTemplate =
          TEXT_MESSAGE_TEMPLATES[
            'Hello! Your NRx order is confirmed and you will be receiving your BeWell Monthly #2 meals soon'
          ];
      } else if (commSubject.startsWith('Hello! Your BeWell Nutrition Consult is booked.')) {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['Hello! Your BeWell Nutrition Consult is booked.'];
      } else if (commSubject === 'APP DOWNLOAD') {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['APP DOWNLOAD EMAIL'];
      } else if (commSubject === 'ZING AFTER INTAKE TEXT') {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['ZING AFTER INTAKE EMAIL'];
      } else if (
        commSubject.startsWith('Hello!. This is NourishedRx.-glucose-text-survey') ||
        commSubject.startsWith('glucose-text-survey')
      ) {
        emailOrTextTemplate = WEB_SURVEYS_BY_EMAIL_TEXT_CONTENT['glucose-text-survey'];
      } else if (commSubject.startsWith('Estimated Delivery Date')) {
        emailOrTextTemplate = TEXT_MESSAGE_TEMPLATES['Estimated Delivery Date'];
      } else if (commSubject in EMAIL_TEXT_NAME_MATCH) {
        const updatedTextTitle = EMAIL_TEXT_NAME_MATCH[commSubject] as string;
        emailOrTextTemplate = REFERRAL_SUBJECT_LINES[updatedTextTitle];
      } else {
        emailOrTextTemplate = 'This text temmplate is not available';
      }
    }
  }
  return emailOrTextTemplate;
};
